import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Dialog, Typography, useMediaQuery } from "@mui/material";
import ShareLink from "../../../components/common/ShareLink";
import { HINDI_FONT } from "../../Temples/shared";
import { getCampaignShareDetails } from "../../../api/services/campaignService";
import { getCampaignUrl } from "../../../utils/slugUtils";
import checkIcon from "../../../assets/Images/TempleDetail/verified-check.svg";
import chipHeart from "../../../assets/Images/TempleThankYou/chip-heart.svg";
import downloadIcon from "../../../assets/Images/TempleThankYou/download.svg";
import shareIcon from "../../../assets/Images/TempleThankYou/share.svg";
import { SkylineTemple, PetalShower } from "./SkylineIntro";
import { C, FONT, money, Detail, DetailsGrid, pillSx, pillPhoneSx, UrgentTemples } from "./shared";
import templeBell from "../../../assets/Images/TempleThankYou/temple-bell-intro.mp3";

// Temple bell rings as the petals start to fall (the shower starts 2.3s in), plays for 3.5s
// and fades out over the last 0.4s. The file is already cut to its first 4s (80KB).
const BELL_START_MS = 2300;
const BELL_LENGTH_S = 3.5;
const BELL_FADE_S = 0.4;
const BELL_VOLUME = 0.8;

const useTempleBell = () => {
  useEffect(() => {
    if (typeof window === "undefined" || typeof Audio === "undefined") return undefined;
    const audio = new Audio(templeBell);
    audio.preload = "auto";
    audio.volume = BELL_VOLUME;
    let raf = null;

    // Fade during the last BELL_FADE_S seconds, then stop at BELL_LENGTH_S.
    const tick = () => {
      const t = audio.currentTime;
      if (t >= BELL_LENGTH_S) {
        audio.pause();
        return;
      }
      const fadeFrom = BELL_LENGTH_S - BELL_FADE_S;
      audio.volume = t > fadeFrom ? Math.max(0, BELL_VOLUME * (1 - (t - fadeFrom) / BELL_FADE_S)) : BELL_VOLUME;
      raf = requestAnimationFrame(tick);
    };

    const start = setTimeout(() => {
      // Browsers can block sound until the visitor has interacted with the site; then we just stay quiet.
      audio
        .play()
        .then(() => {
          raf = requestAnimationFrame(tick);
        })
        .catch(() => {});
    }, BELL_START_MS);

    return () => {
      clearTimeout(start);
      if (raf) cancelAnimationFrame(raf);
      audio.pause();
      audio.src = "";
    };
  }, []);
};

// Thank-you page for temple campaigns — Figma "Temple — Thank you (Generic)" (3576:6650).
// Same data as the regular thank-you page; ThankyouPage decides which one to show.

const TempleThankYou = ({ campaign, campaignId, fallbackTitle, amount, tipAmount = 0, currency = "INR", donorName, dateLabel, transactionId, paymentMethod, isMonthly, on80G }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  // The hero (temple → buttons) fits the window on every screen. Short windows and phones get
  // tighter spacing, and the temple takes whatever height is left, measured after layout.
  const isDesktop = useMediaQuery("(min-width:900px)");
  const compact = useMediaQuery("(max-height:1200px)") && isDesktop;
  // Laptop-height windows (1366×768, 1280×720): tighter still, and the temple may go down to 150px.
  const tight = useMediaQuery("(max-height:820px)") && isDesktop;
  // Short phones (iPhone SE) get a smaller floor so the buttons clear the WhatsApp button.
  const shortPhone = useMediaQuery("(max-height:700px)") && isMobile;
  const minTemple = shortPhone ? 60 : isMobile ? 90 : tight ? 150 : 200;
  // Phones never scale below 80% (76% on short ones) so the summary stays readable.
  const minZoom = shortPhone ? 0.76 : isMobile ? 0.8 : 0.75;
  const heroRef = useRef(null);
  const templeRef = useRef(null);
  const actionsRef = useRef(null);
  const [templeHeight, setTempleHeight] = useState(null);
  // If even a 200px temple doesn't fit, the whole block (temple → buttons) is scaled down evenly,
  // never below 75%. zoomRef keeps the value fit() last applied so it can measure unscaled sizes.
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  useLayoutEffect(() => {
    const fit = () => {
      // Phones follow Figma "Mobile revamp" 802:1481: a normal scrolling page, no scaling
      if (isMobile) {
        zoomRef.current = 1;
        setZoom(1);
        setTempleHeight(199);
        return;
      }
      const hero = heroRef.current;
      const temple = templeRef.current;
      const actions = actionsRef.current;
      if (!hero || !temple || !actions) return;
      const z = zoomRef.current || 1;
      const heroTop = hero.getBoundingClientRect().top + window.scrollY;
      const templeRect = temple.getBoundingClientRect();
      const below = (actions.getBoundingClientRect().bottom - templeRect.bottom) / z; // text, card, buttons (unscaled)
      const above = templeRect.top + window.scrollY - heroTop; // section top padding
      // 24px breathing room under the buttons; phones also keep clear of the floating WhatsApp button
      const free = window.innerHeight - heroTop - above - (isMobile ? 88 : 24);
      let nextHeight;
      let nextZoom = 1;
      if (below + 420 <= free) nextHeight = 420;
      else if (below + minTemple <= free) nextHeight = Math.round(free - below);
      else {
        nextHeight = minTemple;
        nextZoom = Math.max(minZoom, Math.floor((free / (below + minTemple)) * 100) / 100);
      }
      zoomRef.current = nextZoom;
      setZoom(nextZoom);
      setTempleHeight((prev) => (prev && Math.abs(prev - nextHeight) < 2 ? prev : nextHeight));
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (actionsRef.current?.parentElement) ro.observe(actionsRef.current.parentElement);
    window.addEventListener("resize", fit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, [isMobile, minTemple, minZoom]);
  useTempleBell();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);

  const fetchShare = useCallback(async () => {
    if (!campaignId) return;
    setShareLoading(true);
    setShareError(null);
    try {
      setShareInfo(await getCampaignShareDetails(campaignId));
    } catch {
      setShareError("Unable to load share options right now.");
    } finally {
      setShareLoading(false);
    }
  }, [campaignId]);

  const total = Number(amount) || 0;
  const tip = Math.max(0, Number(tipAmount) || 0);
  const seva = Math.max(0, total - tip);
  const tipPct = seva > 0 && tip > 0 ? Math.round((tip / seva) * 100) : 0;
  const shareUrl = campaign ? (() => {
    const url = getCampaignUrl(campaign);
    return url && typeof window !== "undefined" ? `${window.location.origin}${url}` : "";
  })() : "";

  return (
    <Box sx={{ background: "#FFFFFF", overflowX: "clip" }}>
      {/* ── Hero ── */}
      <Box ref={heroRef} component="section" sx={{ position: "relative", pt: isMobile ? "20px" : compact ? "12px" : "30px", pb: isMobile ? "32px" : tight ? "64px" : "104px", px: "16px", ...(isMobile && { background: "linear-gradient(180deg, #FCF6ED 0px, #FFFFFF 239px)" }) }}>
        <PetalShower />
        <Box sx={{ zoom }}>
        <Box ref={templeRef}>
          <SkylineTemple height={templeHeight} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1, maxWidth: "900px", mx: "auto", mt: isMobile ? "4px" : tight ? "8px" : compact ? "12px" : "22px", px: isMobile ? "8px" : 0, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <Typography sx={{ fontFamily: HINDI_FONT, fontSize: isMobile ? "15px" : "18px", lineHeight: isMobile ? "normal" : "26px", color: C.gold }}>॥ सेवा परमो धर्मः ॥</Typography>
          <Typography component="h1" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "28px" : tight ? "36px" : compact ? "42px" : "52px", lineHeight: isMobile ? "34px" : tight ? "44px" : compact ? "50px" : "62px", color: C.ink, mt: isMobile ? "6px" : tight ? "2px" : compact ? "6px" : "12px" }}>
            Thank you for your{" "}
            <Box component="span" sx={{ fontStyle: "italic", color: C.red }}>
              seva.
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: isMobile ? "14px" : tight ? "15px" : compact ? "16px" : "17px", lineHeight: isMobile ? "21px" : tight ? "22px" : compact ? "24px" : "27px", color: C.muted, mt: isMobile ? "6px" : tight ? "4px" : compact ? "8px" : "12px", maxWidth: "640px" }}>
            Your offering helps restore and care for India's sacred temples. A receipt has been sent to your email.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: isMobile ? "14px" : tight ? "8px" : compact ? "10px" : "16px", pl: isMobile ? "12px" : "14px", pr: isMobile ? "14px" : "16px", py: isMobile ? "6px" : tight ? "6px" : "8px", borderRadius: "100px", background: "rgba(22,163,74,0.08)" }}>
            <Box component="img" src={checkIcon} alt="" sx={{ width: isMobile ? "15px" : "16px", height: isMobile ? "15px" : "16px" }} />
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: "#15803D" }}>Payment successful</Typography>
          </Box>

          {/* Donation summary */}
          <Box
            sx={{
              mt: isMobile ? "20px" : tight ? "12px" : compact ? "18px" : "30px",
              width: isMobile ? "calc(100% + 16px)" : "100%",
              maxWidth: "600px",
              boxSizing: "border-box",
              textAlign: "left",
              background: "#FFFFFF",
              border: "1px solid rgba(201,149,43,0.3)",
              borderRadius: "20px",
              boxShadow: isMobile ? "0px 12px 30px 0px rgba(89,51,13,0.07)" : "0px 16px 40px 0px rgba(89,51,13,0.07)",
              px: isMobile ? "18px" : "32px",
              py: isMobile ? "20px" : tight ? "16px" : compact ? "20px" : "28px",
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "14px" : tight ? "10px" : compact ? "12px" : "18px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "10px" : "11px", lineHeight: isMobile ? "normal" : "14px", letterSpacing: isMobile ? "1.2px" : "1.32px", color: C.gold }}>DONATION SUMMARY</Typography>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "15px" : "17px", lineHeight: isMobile ? "20px" : "24px", color: C.ink, mt: "2px", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {campaign?.title || fallbackTitle || "Temple seva"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", pl: "10px", pr: "12px", py: "6px", borderRadius: "100px", background: "rgba(236,27,38,0.07)", flexShrink: 0 }}>
                <Box component="img" src={chipHeart} alt="" sx={{ width: "13px", height: "13px" }} />
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "12px", lineHeight: "16px", color: C.red, whiteSpace: "nowrap" }}>{isMonthly ? "Monthly" : "One-time"}</Typography>
              </Box>
            </Box>

            <Box sx={{ background: C.cream, borderRadius: "14px", px: isMobile ? "16px" : "20px", py: isMobile ? "16px" : tight ? "12px" : "18px", display: "flex", flexDirection: "column", gap: isMobile ? "5px" : tight ? "8px" : "12px" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "normal" : "20px", color: C.muted }}>{isMonthly ? "Paid this month" : "Total paid"}</Typography>
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "20px" : "32px", lineHeight: isMobile ? "normal" : "38px", color: C.ink, whiteSpace: "nowrap" }}>{money(total, currency)}</Typography>
              </Box>
              {tip > 0 && (
                <>
                  <Box sx={{ height: "1px", background: "rgba(201,149,43,0.25)" }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.muted }}>Temple seva</Typography>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.ink }}>{money(seva, currency)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 500, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.muted }}>TrueHope support{tipPct ? ` (${tipPct}%)` : ""}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "12px" : "13px", lineHeight: isMobile ? "normal" : "18px", color: C.ink }}>{money(tip, currency)}</Typography>
                  </Box>
                </>
              )}
            </Box>

            <DetailsGrid phone={isMobile} isDesktop={isDesktop}>
              <Detail phone={isMobile} label="DONOR" value={donorName} />
              <Detail phone={isMobile} label="DATE" value={dateLabel} />
              <Detail phone={isMobile} label="TRANSACTION ID" value={transactionId} />
              <Detail phone={isMobile} label="PAYMENT" value={paymentMethod} />
            </DetailsGrid>
          </Box>

          {/* Actions */}
          <Box ref={actionsRef} sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "10px" : "12px", mt: isMobile ? "20px" : tight ? "12px" : compact ? "18px" : "32px", flexWrap: isMobile ? "nowrap" : "wrap", justifyContent: "center", width: isMobile ? "calc(100% + 16px)" : "auto" }}>
            <Box component="button" type="button" onClick={on80G} sx={{ ...pillSx, ...(isMobile && pillPhoneSx), border: "none", background: C.red, color: "#FFFFFF", boxShadow: "0px 8px 20px rgba(236,27,38,0.25)" }}>
              <Box component="img" src={downloadIcon} alt="" sx={{ width: "16px", height: "16px" }} />
              Download 80G form
            </Box>
            {campaignId && (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  if (!shareInfo && !shareLoading) fetchShare();
                  setShareOpen(true);
                }}
                sx={{ ...pillSx, ...(isMobile && pillPhoneSx), border: `1px solid ${C.sand}`, background: "#FFFFFF", color: C.ink }}
              >
                <Box component="img" src={shareIcon} alt="" sx={{ width: "16px", height: "16px" }} />
                Share the campaign
              </Box>
            )}
          </Box>
        </Box>
        </Box>
      </Box>

      <UrgentTemples campaignId={campaignId} />

      <Dialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        maxWidth={isMobile ? "xs" : "md"}
        fullWidth
        PaperProps={{ sx: { background: "transparent", boxShadow: "none", m: isMobile ? "16px" : "auto" } }}
      >
        <ShareLink shareData={shareInfo} fallbackUrl={shareUrl} isLoading={shareLoading} error={shareError} onRetry={fetchShare} onClose={() => setShareOpen(false)} />
      </Dialog>
    </Box>
  );
};

export default TempleThankYou;
